import { HTMLAttributes, ReactNode } from "react";
import urlJoin from "url-join";
import { useChat } from "react-live-chat-loader";
import { BsQuestionCircleFill } from "react-icons/bs";
import { cn } from "src/utils/cn";
import { isBrowser } from "src/libs/nextLib";
import config from "src/config";

function openTab(url: string) {
  if (!isBrowser()) {
    return;
  }

  window.open(url, "_blank");
}

function openContactUsTab() {
  openTab(urlJoin(config.helpSite.baseUrl, "#contact"));
}

function openHelpHomeTab() {
  openTab(config.helpSite.baseUrl);
}

function openHelpArticleTab(articleId: string) {
  if (!isBrowser()) {
    return;
  } else if (!articleId) {
    openHelpHomeTab();
  } else {
    fetch(urlJoin(config.helpSite.baseUrl, `article/${articleId}`))
      .then((res) => res.json())
      .then((data) => {
        const url =
          data.publicUrl ||
          urlJoin(
            config.helpSite.baseUrl,
            `article/${data.number}-${data.slug}`
          );

        openTab(url);
      })
      .catch((err) => {
        console.error(`Error fetching help article ID ${articleId}:`, err);

        openTab(config.helpSite.baseUrl);
      });
  }
}

type HelpButtonVariant = "link" | "button";

const buttonBaseStyles = `cursor-pointer font-normal`;

const buttonVariantStyles: Record<HelpButtonVariant, string> = {
  link: `inline underline hover:no-underline focus:no-underline`,
  button: `inline-block text-sm px-3 py-1 border text-red-500 border-red-500 rounded-md`,
};

type HelpButtonProps = HTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: HelpButtonVariant;
  articleId?: string;
  contactUs?: boolean;
  openInBeacon?: boolean;
};

declare const window: Window &
  typeof globalThis & {
    Beacon: any;
  };

export function HelpButton({
  children,
  variant = "link",
  articleId,
  contactUs,
  onClick = () => null,
  openInBeacon = true,
  ...props
}: HelpButtonProps) {
  const [, loadChat] = useChat();

  const isChatEnabled = config.helpScout.enabled;

  return (
    <button
      type="button"
      className={cn([buttonBaseStyles, buttonVariantStyles[variant]])}
      onClick={(e) => {
        if (articleId) {
          if (
            openInBeacon &&
            // if the beacon isn't enabled, we can't open the article in it
            isChatEnabled
          ) {
            loadChat({ open: true });
            window.Beacon("article", articleId);
          } else {
            openHelpArticleTab(articleId);
          }
        } else if (contactUs) {
          if (openInBeacon && isChatEnabled) {
            loadChat({ open: true });
            window.Beacon("navigate", "/ask/");
          } else {
            openContactUsTab();
          }
        } else {
          if (openInBeacon && isChatEnabled) {
            loadChat({ open: true });
          } else {
            openHelpHomeTab();
          }
        }

        onClick(e);
      }}
      {...props}
    >
      <div className="flex items-center gap-2">
        {variant === "button" ? <BsQuestionCircleFill /> : null}
        <div>{children}</div>
      </div>
    </button>
  );
}

export default HelpButton;
